import {
  PreorderResult,
  SearchPreordersRequest,
  TrackedProduct,
  TrackProductInput,
  CheckoutLink,
  PriceHistory,
  EnrichedCatalogProduct,
} from '../types';

const BASE = '/api';

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const method = (init?.method ?? 'GET').toUpperCase();
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'same-origin',
    ...init,
  });

  // Session expired or never authenticated — drop to /login so the user can re-auth
  if (res.status === 401 && !location.pathname.startsWith('/login')) {
    const next = encodeURIComponent(location.pathname + location.search);
    location.replace(`/login?next=${next}`);
    throw new Error('Session expired — redirecting to login…');
  }

  let data: Record<string, unknown> = {};
  try { data = await res.json() as Record<string, unknown>; }
  catch { /* non-JSON response (e.g. HTML error page) — fall through to generic error */ }

  if (!res.ok) {
    const detail = (data.error as string | undefined) ?? `HTTP ${res.status}`;
    throw new Error(`${detail} (${method} ${path})`);
  }
  return data as T;
}

export async function searchPreorders(
  req: SearchPreordersRequest
): Promise<{ results: PreorderResult[]; message?: string }> {
  return apiFetch('/search-preorders', {
    method: 'POST',
    body: JSON.stringify(req),
  });
}

export async function getTrackedProducts(): Promise<{ trackedProducts: TrackedProduct[] }> {
  return apiFetch('/tracked-products');
}

export async function addTrackedProduct(
  input: TrackProductInput
): Promise<{ trackedProduct: TrackedProduct }> {
  return apiFetch('/tracked-products', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateTrackedProduct(
  id: string,
  partial: Partial<TrackProductInput>
): Promise<{ trackedProduct: TrackedProduct }> {
  return apiFetch(`/tracked-products/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(partial),
  });
}

export async function deleteTrackedProduct(id: string): Promise<{ success: boolean }> {
  return apiFetch(`/tracked-products/${id}`, { method: 'DELETE' });
}

export async function getCheckoutLinks(
  productId: string,
  region?: string,
): Promise<{ productId: string; links: CheckoutLink[]; stockVerifiedAt: string | null }> {
  const qs = region ? `?region=${encodeURIComponent(region)}` : '';
  return apiFetch(`/checkout/${encodeURIComponent(productId)}${qs}`);
}

/** Session-authed manual refresh (UI button). Returns 202 immediately; the
 *  scrape continues in the background. */
export async function triggerManualRefresh(): Promise<{ ok: boolean; productCount: number; message: string }> {
  return apiFetch('/refresh-now', { method: 'POST' });
}

export async function getRefreshStatus(): Promise<{ lastRefresh: { startedAt: string; finishedAt: string; productCount: number; ok: number; failed: number } | null }> {
  return apiFetch('/refresh-now');
}

export async function getPriceHistory(productId: string): Promise<PriceHistory> {
  return apiFetch(`/price-history/${encodeURIComponent(productId)}`);
}

export async function getEnrichedProduct(productId: string): Promise<EnrichedCatalogProduct> {
  return apiFetch(`/catalog/${encodeURIComponent(productId)}`);
}

export async function sendTestNotification(
  channel: 'email' | 'telegram',
  email?: string,
  telegramChatId?: string
): Promise<{ success: boolean; message: string }> {
  return apiFetch('/notifications/test', {
    method: 'POST',
    body: JSON.stringify({ channel, email, telegramChatId }),
  });
}
